import { KpiTint } from "@/shell/components/KpiCard";
import {
    OrdersCustomersResponse,
    OrdersMonetizationResponse,
    OrdersSegmentationResponse,
    OrdersSummaryResponse,
} from "@/features/orders-analytics/types/orders-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

export type RevenueKpiVM = {
    icon: string;
    label: string;
    value: string;
    sub: string;
    mom: number | null;
    tint: KpiTint;
};

export type TicketRowVM = {
    range: string;
    orders: number;
    volume: string;
    revenue: string;
    revenueNumeric: number;
    takeRate: string;
    success: string;
    p95: string;
};

export type PricingRowVM = {
    segment: string;
    volume: string;
    revenue: string;
    takeRate: string;
    benchmark: string;
    opportunity: string;
};

export type RevenueVM = {
    kpis: RevenueKpiVM[];
    ticketRows: TicketRowVM[];
    pricingRows: PricingRowVM[];
    pricingEmpty: boolean;
};

const MUTED_VALUE = "—";
const MUTED_SUB = "Sin datos disponibles";

type FromDataInput = {
    monetization: OrdersMonetizationResponse | null;
    summary: OrdersSummaryResponse | null;
    customers: OrdersCustomersResponse | null;
    pricingSegmentation: OrdersSegmentationResponse | null;
};

const emptyKpi = (icon: string, label: string, tint: KpiTint = "muted"): RevenueKpiVM => ({
    icon,
    label,
    value: MUTED_VALUE,
    sub: MUTED_SUB,
    mom: null,
    tint,
});

const derivedTakeRatePercentage = (revenue: number, volume: number): number | null =>
    volume > 0 ? (revenue / volume) * 100 : null;

const buildTakeRateKpi = (summary: OrdersSummaryResponse): RevenueKpiVM => {
    const currentTakeRate = derivedTakeRatePercentage(
        summary.businessRevenue.current,
        summary.volume.current
    );
    const previousTakeRate = derivedTakeRatePercentage(
        summary.businessRevenue.previous,
        summary.volume.previous
    );

    if (currentTakeRate === null) {
        return emptyKpi("percent", "Take rate", "primary");
    }

    const mom =
        previousTakeRate === null || previousTakeRate === 0
            ? null
            : ((currentTakeRate - previousTakeRate) / previousTakeRate) * 100;

    return {
        icon: "percent",
        label: "Take rate",
        value: `${(currentTakeRate * 100).toFixed(0)} bps`,
        sub:
            previousTakeRate === null
                ? "derived: revenue / volume"
                : `vs previous ${(previousTakeRate * 100).toFixed(0)} bps`,
        mom,
        tint: "primary",
    };
};

const fromData = (input: FromDataInput): RevenueVM => {
    const { monetization, summary, customers, pricingSegmentation } = input;

    const kpis: RevenueKpiVM[] = [
        summary === null || monetization === null
            ? emptyKpi("payments", "Gross revenue MTD", "success")
            : {
                  icon: "payments",
                  label: "Gross revenue MTD",
                  value: formatters.formatCurrency(monetization.totalBusinessRevenue),
                  sub: `vs previous ${formatters.formatCurrency(summary.businessRevenue.previous)}`,
                  mom: summary.businessRevenue.deltaPercentage,
                  tint: "success",
              },
        emptyKpi("savings", "Net revenue MTD", "success"),
        summary === null ? emptyKpi("percent", "Take rate", "primary") : buildTakeRateKpi(summary),
        summary === null
            ? emptyKpi("shopping_cart", "Avg ticket", "primary")
            : {
                  icon: "shopping_cart",
                  label: "Avg ticket",
                  value: formatters.formatCurrency(summary.averageTicket.current),
                  sub: `vs previous ${formatters.formatCurrency(summary.averageTicket.previous)}`,
                  mom: summary.averageTicket.deltaPercentage,
                  tint: "primary",
              },
        customers === null
            ? emptyKpi("domain", "Revenue / workspace", "primary")
            : {
                  icon: "domain",
                  label: "Revenue / workspace",
                  value: formatters.formatCurrency(customers.businessRevenuePerUser),
                  sub: "current period",
                  mom: null,
                  tint: "primary",
              },
        emptyKpi("tune", "Pricing health", "muted"),
        emptyKpi("swap_horiz", "FX revenue share", "violet"),
        emptyKpi("request_quote", "Refunds / disputes", "violet"),
    ];

    const ticketRows: TicketRowVM[] =
        monetization === null
            ? []
            : monetization.ticketSizeDistribution.map((bucket) => {
                  const takeRate = derivedTakeRatePercentage(bucket.businessRevenue, bucket.volume);
                  return {
                      range: bucket.key,
                      orders: bucket.orders,
                      volume: formatters.formatCurrency(bucket.volume),
                      revenue: formatters.formatCurrency(bucket.businessRevenue),
                      revenueNumeric: bucket.businessRevenue,
                      takeRate: takeRate === null ? MUTED_VALUE : `${takeRate.toFixed(2)}%`,
                      success: MUTED_VALUE,
                      p95: MUTED_VALUE,
                  };
              });

    const pricingRows: PricingRowVM[] =
        pricingSegmentation === null
            ? []
            : pricingSegmentation.rows.map((row) => {
                  const takeRate = derivedTakeRatePercentage(row.businessRevenue, row.volume);
                  return {
                      segment: row.segment,
                      volume: formatters.formatCurrency(row.volume),
                      revenue: formatters.formatCurrency(row.businessRevenue),
                      takeRate: takeRate === null ? MUTED_VALUE : `${takeRate.toFixed(2)}%`,
                      benchmark: MUTED_VALUE,
                      opportunity: MUTED_VALUE,
                  };
              });

    return {
        kpis,
        ticketRows,
        pricingRows,
        pricingEmpty: pricingRows.length === 0,
    };
};

export const revenueViewModel = { fromData };
